// The wizard's serialization machinery. Everything template-specific — which
// sections exist, which fields they need — lives in templates/, and reaches this
// file as the `template` argument.

import format from "@app/lib/format";

// The pseudo-section for fields that are not tied to one generated section. Its
// panel is always rendered, above the per-section ones.
export const GENERAL_SECTION = "general";

// The typed half of the contract's `[metadata]` table — these go to `metadata`,
// where the backend model and route schema declare their shape. Anything not
// listed here (and not UI-only) is a free-form scalar and goes to
// `extraMetadata`, which the backend merges into `metadata` flat.
//
// Shared rather than per-template: `[metadata]` is one shape on the backend
// whichever document is being generated. What varies is which of them a template
// asks for, and that is its `fields` list.
const METADATA_KEYS = new Set([
    "device_names",
    "manufacturer",
    "intended_purpose",
    "clinical_study_titles",
    "biocompatibility_tests",
    "non_clinical_tests",
    "equivalent_device_names",
    "launch_country",
    "date_format",
    "launch_date",
]);

// Form keys serialized explicitly and never dumped into the metadata bag: the
// selection and the section maps are emitted as top-level tables, and
// `section_priority_enabled` is a wizard-only aid derived back out of them.
//
// A template adds its own UI-only field names through `nonMetadataKeys` — the
// CER's three `device_family_*` fields, for instance, which feed the typed
// `device_family[]` array and are a 400 if they reach `[metadata]`.
const SECTION_KEYS = new Set([
    "sections_selected",
    "section_documents",
    "section_priority",
    "section_priority_enabled",
    "section_articles",
]);

const isEmpty = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

// Clean a control's value for the wire. Half-filled rows in the two list
// controls are the wizard's own placeholders — sending them means an empty string
// or a nameless entry reaching the engine, which either fails its schema or
// renders as a blank line in the regulatory document. Unset is the convention for
// "no value": omit the key rather than send a placeholder.
function sanitize(field, value) {
    if (field.type === "string_list") {
        return (Array.isArray(value) ? value : [])
            .map((v) => (typeof v === "string" ? v.trim() : v))
            .filter((v) => v !== "" && v !== null && v !== undefined);
    }

    if (field.type === "clinical_entry_list") {
        return (Array.isArray(value) ? value : [])
            .map((entry) => {
                const out = { name: (entry?.name || "").trim() };
                if (entry?.device?.trim()) {
                    out.device = entry.device.trim();
                }
                if (entry?.source_document?.trim()) {
                    out.source_document = entry.source_document.trim();
                }
                return out;
            })
            .filter((entry) => entry.name);
    }

    return value;
}

const toUTC = (field, value) => {
    if (field.type === "date") {
        return format.toUTC(value).toISOString();
    }

    if (field.type === "date_range") {
        return value.map((date) => (date ? format.toUTC(date).toISOString() : null));
    }

    return value;
};

const toLocal = (field, value) => {
    if (field.type === "date") {
        return format.toLocal(value).toISOString();
    }

    if (field.type === "date_range") {
        return value.map((date) => (date ? format.toLocal(date).toISOString() : null));
    }

    return value;
};

// The sections a save writes entries for. A template with fixed sections has no
// picker to read — all of them are always generated — so it says so once here
// rather than relying on the form state having been seeded correctly.
const selectedSections = (formValues, template) =>
    template.fixedSections ||
    (Array.isArray(formValues.sections_selected) ? formValues.sections_selected : []);

/**
 * Build the PUT body from the wizard's flat form values.
 *
 * `knownDocumentIds` / `knownArticleIds`, when given, restrict the per-section
 * assignments to inputs that still exist in the project — one deleted after it
 * was assigned would otherwise fail the run with a "missing documents" error.
 * Pass null for a list that has not loaded yet, or a save would prune every
 * assignment against an empty set.
 */
export function toPayload(formValues, knownDocumentIds = null, knownArticleIds = null, template) {
    const metadata = {};
    const extraMetadata = {};
    // Seeded from the template rather than filled in as fields are found: the PUT
    // is a whole-document replace, so a table the user has emptied still has to be
    // sent, as `{}`, to blank the stored one.
    const tables = Object.fromEntries((template.payloadTables || []).map((t) => [t, {}]));

    for (const field of template.fields) {
        if (SECTION_KEYS.has(field.name) || template.nonMetadataKeys.has(field.name)) {
            continue;
        }

        const value = sanitize(field, formValues[field.name]);
        if (isEmpty(value)) {
            continue;
        }

        const serialized = toUTC(field, value);

        // A field belonging to a config table of its own — `[sota]`,
        // `[stability_data]`. `wireName` exists because some of those keys collide
        // by name with `[metadata]` ones and need a unique key in the flat form.
        if (field.group) {
            tables[field.group][field.wireName || field.name] = serialized;
        } else if (METADATA_KEYS.has(field.name)) {
            metadata[field.name] = serialized;
        } else {
            extraMetadata[field.name] = serialized;
        }
    }

    // Per-section tables (kept in id space; the backend translates ids to
    // filenames when building the run payload). Written for every section the
    // user has ever configured, not only the selected ones: deselecting a section
    // stops it generating and keeps its inputs, and `sections_selected` is what
    // records the choice. The backend projects these onto the selection when it
    // builds the run payload.
    const selected = selectedSections(formValues, template);
    const known = knownDocumentIds ? new Set(knownDocumentIds) : null;
    const knownArticles = knownArticleIds ? new Set(knownArticleIds) : null;
    const rawDocuments = formValues.section_documents || {};
    const rawPriority = formValues.section_priority || {};
    const priorityEnabled = formValues.section_priority_enabled || {};
    const rawArticles = formValues.section_articles || {};

    const section_documents = {};
    const section_priority = {};
    const section_articles = {};

    const configured = [
        ...new Set([
            ...selected,
            ...Object.keys(rawDocuments),
            ...Object.keys(rawArticles),
            ...Object.keys(rawPriority),
        ]),
    ];

    for (const key of configured) {
        // A section is scoped by articles or by documents, never both. Order is
        // preserved either way: for the literature section it *is* the §6.2.1
        // article numbering.
        if (template.sectionArticleType[key]) {
            section_articles[key] = (Array.isArray(rawArticles[key])
                ? rawArticles[key]
                : []
            ).filter((id) => !knownArticles || knownArticles.has(id));
            continue;
        }

        if (template.sectionsWithoutDocuments.has(key)) {
            section_documents[key] = [];
            continue;
        }

        const assigned = (Array.isArray(rawDocuments[key]) ? rawDocuments[key] : []).filter(
            (id) => !known || known.has(id),
        );
        section_documents[key] = assigned;

        if (priorityEnabled[key]) {
            const scope = new Set(assigned);
            // Stored even when it does not cover the section's whole scope, so a
            // half-finished ranking survives a save. Sending a partial one would
            // exclude the documents it omits, and `buildSectionPriority` drops it
            // at the request boundary for exactly that reason.
            const ranked = (Array.isArray(rawPriority[key]) ? rawPriority[key] : []).filter((id) =>
                scope.has(id),
            );
            if (ranked.length) {
                section_priority[key] = ranked;
            }
        }
    }

    // Derive the typed device_family array from the three flat fields the wizard
    // collects. Empty arrays are fine — backend tolerates them.
    const device_family = [];
    if (formValues.device_family_type || formValues.device_family_list) {
        device_family.push({
            device_family_name: formValues.device_family_type || "",
            category: formValues.device_family_category || undefined,
            device_list: (formValues.device_family_list || "")
                .split(/\r?\n|,/g)
                .map((s) => s.trim())
                .filter(Boolean),
        });
    }

    return {
        metadata,
        extraMetadata,
        // Sent as collected, even half-filled, so a Section 4 the user is partway
        // through survives a save. The engine's all-or-nothing rule — an absent
        // `[sota]` table skips Section 4 cleanly, an incomplete one aborts the run
        // — is applied at the request boundary in `buildSota`, not here.
        ...tables,
        ...(template.emitsDeviceFamily ? { device_family } : {}),
        sections_selected: selected,
        section_documents,
        section_priority,
        section_articles,
    };
}

export function fromConfig(config, template) {
    // A template with fixed sections has to open with them already selected —
    // there is no picker for the user to select them in, and their panels are the
    // whole form.
    const fixed = template.fixedSections;

    if (!config) {
        return fixed
            ? {
                  sections_selected: [...fixed],
                  section_documents: {},
                  section_priority: {},
                  section_articles: {},
                  section_priority_enabled: {},
              }
            : {};
    }

    const values = {
        ...(config.metadata || {}),
        ...(config.extraMetadata || {}),
    };

    for (const field of template.fields) {
        if (field.group || (field.type !== "date" && field.type !== "date_range")) {
            continue;
        }
        if (!isEmpty(values[field.name])) {
            values[field.name] = toLocal(field, values[field.name]);
        }
    }

    const devFamily = (config.device_family || [])[0];
    if (devFamily) {
        if (!values.device_family_type) {
            values.device_family_type = devFamily.device_family_name;
        }
        if (!values.device_family_category) {
            values.device_family_category = devFamily.category;
        }
        if (!values.device_family_list && Array.isArray(devFamily.device_list)) {
            values.device_family_list = devFamily.device_list.join("\n");
        }
    }

    // Un-nest each config table back onto the flat form keys.
    for (const field of template.fields) {
        if (!field.group) {
            continue;
        }
        const table = config[field.group] || {};
        const stored = table[field.wireName || field.name];
        if (!isEmpty(stored)) {
            values[field.name] = stored;
        }
    }

    // Rehydrate the per-section tables (id-keyed, matching the pickers). Priority
    // is "enabled" for any section present in section_priority.
    const sectionDocuments = config.section_documents || {};
    const sectionPriority = config.section_priority || {};
    const sectionArticles = config.section_articles || {};

    values.section_documents = { ...sectionDocuments };
    values.section_priority = { ...sectionPriority };
    values.section_articles = { ...sectionArticles };
    values.sections_selected = fixed ? [...fixed] : storedSelection(config);
    values.section_priority_enabled = Object.keys(sectionPriority).reduce((acc, key) => {
        acc[key] = true;
        return acc;
    }, {});

    return values;
}

export function mergeFromConfig(formValues, config, template, { sectionKey = null } = {}) {
    const incoming = fromConfig(config, template);
    const merged = { ...formValues };

    const fieldNames = template.fields
        .filter((field) => !SECTION_KEYS.has(field.name))
        .filter((field) => !sectionKey || field.section === sectionKey)
        .map((field) => field.name);

    for (const name of fieldNames) {
        if (!isEmpty(incoming[name])) {
            merged[name] = incoming[name];
        }
    }

    const sectionMaps = ["section_documents", "section_priority", "section_articles"];
    const copiedSections = sectionKey ? [sectionKey] : storedSelection(config);

    for (const map of sectionMaps) {
        const next = { ...(merged[map] || {}) };
        for (const key of copiedSections) {
            if (incoming[map]?.[key] !== undefined) {
                next[key] = incoming[map][key];
            }
        }
        merged[map] = next;
    }

    merged.section_priority_enabled = Object.keys(merged.section_priority || {}).reduce(
        (acc, key) => {
            acc[key] = true;
            return acc;
        },
        {},
    );

    const selected = Array.isArray(merged.sections_selected) ? merged.sections_selected : [];
    merged.sections_selected = template.fixedSections
        ? [...template.fixedSections]
        : [...new Set([...selected, ...copiedSections])];

    return merged;
}

export function storedSelection(config) {
    if (Array.isArray(config?.sections_selected)) {
        return [...config.sections_selected];
    }
    return [
        ...new Set([
            ...Object.keys(config?.section_documents || {}),
            ...Object.keys(config?.section_articles || {}),
        ]),
    ];
}

export function hasSectionData(config, template, sectionKey) {
    const maps = ["section_documents", "section_priority", "section_articles"];
    if (maps.some((map) => (config?.[map]?.[sectionKey] || []).length)) {
        return true;
    }

    const values = fromConfig(config, template);
    return template.fields
        .filter((field) => field.section === sectionKey)
        .some((field) => !isEmpty(values[field.name]));
}
